/**
 * Data layer for `three-tier-pricing-starter-agency-enterprise`. Thin typed fetch wrappers over /api/v1/three-tier-pricing-starter-agency-enterprise.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ThreeTierPricingStarterAgencyEnterpriseRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListThreeTierPricingStarterAgencyEnterpriseResponse {
	count: number;
	items: ThreeTierPricingStarterAgencyEnterpriseRow[];
}

export interface CreateThreeTierPricingStarterAgencyEnterpriseBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/three-tier-pricing-starter-agency-enterprise";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListThreeTierPricingStarterAgencyEnterpriseResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListThreeTierPricingStarterAgencyEnterpriseResponse>(res);
}

export async function create(body: CreateThreeTierPricingStarterAgencyEnterpriseBody): Promise<ThreeTierPricingStarterAgencyEnterpriseRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ThreeTierPricingStarterAgencyEnterpriseRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const threeTierPricingStarterAgencyEnterpriseQueryKeys = {
	all: ["three-tier-pricing-starter-agency-enterprise"] as const,
	list: () => [...threeTierPricingStarterAgencyEnterpriseQueryKeys.all] as const,
	detail: (id: string) => [...threeTierPricingStarterAgencyEnterpriseQueryKeys.all, id] as const,
};
